import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { CarpoolEntry } from "../api/types";

/**
 * Per-meet carpool board: offers (drivers with seats) and requests (need a
 * ride). Team-scoped on the backend. Users can post one of each and withdraw
 * their own entries.
 */
export function Carpool({ meetId, myUserId }: { meetId: string; myUserId?: string }) {
  const [offers, setOffers] = useState<CarpoolEntry[]>([]);
  const [requests, setRequests] = useState<CarpoolEntry[]>([]);
  const [msg, setMsg] = useState<string>();
  const [kind, setKind] = useState<"offer" | "request">("offer");
  const [seats, setSeats] = useState("3");
  const [pickupArea, setPickupArea] = useState("");
  const [departureTime, setDepartureTime] = useState("");
  const [notes, setNotes] = useState("");

  const load = () => {
    api
      .listCarpool(meetId)
      .then((d) => {
        setOffers(d.offers);
        setRequests(d.requests);
      })
      .catch((e) => setMsg((e as Error).message));
  };
  useEffect(load, [meetId]);

  const post = async () => {
    setMsg(undefined);
    try {
      await api.postCarpool(meetId, {
        kind,
        seats: kind === "offer" ? Number(seats) || 0 : undefined,
        pickupArea: pickupArea || undefined,
        departureTime: departureTime || undefined,
        notes: notes || undefined,
      });
      setPickupArea("");
      setDepartureTime("");
      setNotes("");
      load();
    } catch (e) {
      setMsg((e as Error).message);
    }
  };

  const remove = async (entryId: string) => {
    await api.deleteCarpool(meetId, entryId).catch(() => undefined);
    load();
  };

  const row = (e: CarpoolEntry) => (
    <div key={e.entryId} className="row spread" style={{ alignItems: "flex-start" }}>
      <div>
        <strong>{e.displayName ?? "A parent"}</strong>
        {e.kind === "offer" && typeof e.seats === "number" && (
          <span className="muted"> · {e.seats} seat{e.seats === 1 ? "" : "s"}</span>
        )}
        <div className="muted" style={{ fontSize: "0.85rem" }}>
          {e.pickupArea && <>📍 {e.pickupArea} </>}
          {e.departureTime && <>· 🕒 {e.departureTime} </>}
          {e.notes && <>· {e.notes}</>}
        </div>
      </div>
      {e.userId === myUserId && (
        <button className="secondary" onClick={() => remove(e.entryId)}>
          Remove
        </button>
      )}
    </div>
  );

  return (
    <div className="card">
      <h2>Carpools</h2>

      <div className="grid" style={{ marginBottom: "0.75rem" }}>
        <div>
          <strong>Offering rides</strong>
          {offers.length === 0 && <p className="muted">No ride offers yet.</p>}
          {offers.map(row)}
        </div>
        <div>
          <strong>Need a ride</strong>
          {requests.length === 0 && <p className="muted">No ride requests yet.</p>}
          {requests.map(row)}
        </div>
      </div>

      <div className="card" style={{ background: "var(--bg)" }}>
        <div className="row">
          <select
            style={{ width: "auto" }}
            value={kind}
            onChange={(e) => setKind(e.target.value as "offer" | "request")}
          >
            <option value="offer">I can drive</option>
            <option value="request">I need a ride</option>
          </select>
          {kind === "offer" && (
            <input
              type="number"
              min={0}
              max={20}
              style={{ width: "6rem" }}
              value={seats}
              onChange={(e) => setSeats(e.target.value)}
              title="Seats available"
            />
          )}
        </div>
        <input
          style={{ marginTop: "0.5rem" }}
          placeholder="Pickup area (e.g. North HS lot)"
          value={pickupArea}
          onChange={(e) => setPickupArea(e.target.value)}
        />
        <input
          style={{ marginTop: "0.5rem" }}
          placeholder="Departure time (e.g. 6:30 AM)"
          value={departureTime}
          onChange={(e) => setDepartureTime(e.target.value)}
        />
        <input
          style={{ marginTop: "0.5rem" }}
          placeholder="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <div style={{ marginTop: "0.5rem" }}>
          <button onClick={post}>
            {kind === "offer" ? "Offer a ride" : "Request a ride"}
          </button>
          {msg && <span className="error" style={{ marginLeft: "0.5rem" }}>{msg}</span>}
        </div>
      </div>
    </div>
  );
}
